import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import { useSWRConfig } from 'swr';
import {
  fetchFollowedGoals,
  fetchMyInfo,
  fetchPurchasedItems
} from '@actions/auth';
import cookies from '@utils/cookies';
import { segment } from '@actions/analytics';
import constants, {
  LOGIN_SUCCESSFUL,
  SIGNUP_SUCCESFUL
} from '@cont/Login/constants';
import validatePresetRedirectURL from '@utils/validateRedirectUrl';
import useLoginStore from '@stores/login/loginStore';
import { getIsRegistered, getSetIsLoggingIn } from '@stores/selectors/login';
import checkWhiteListed from '@utils/whitelistRedirectUrls';
import pushRoute from '@utils/pushRoute';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { checkIsFollowedGoal } from '@cont/App/initialize';
import { useUpdateSelectedGoal } from '@api/hooks/topology/useSelectedGoal';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import useYoutubeCourses from '@api/hooks/youtube-lesson/useYoutubeCourses';
import { fetchTopologyInfo } from '@actions/topologies';
import { getRedirectionURLAfterPostLogin } from '@cont/NewPreSubscribed/utils';
import segmentStorage from '@events/segmentStorage';
import useLoginExperimentEventProps from '@hooks/segment/useLoginExperimentEventProps';
import * as amplitude from '@amplitude/analytics-browser';
import useRewardEventStore from '@stores/rewards';
import sendSegmentEventsPostLogin from '../sendSegmentEventsPostLogin';
import getSelectedGoalFromMeResp from '../getSelectedGoalFromMeResp';
import setUserDetailsBeforeEvent from '../setUserDetailsBeforeSegmentEvent';
import sendIframeLoginSuccessMessage from '../iframelogin/sendIframeLoginSuccessMessage';
import sendPopupLoginSuccessMessage from '../popuplogin/sendPopupLoginSuccessMessage';
import useIsLoginTriggeredFromIframe from './useIsLoginTriggeredFromIframe';

const usePostLoginActions = ({
  country,
  mobileNumber,
  presetRedirectURL,
  loginType,
  loginCallback
}) => {
  const { selectedCity } = useRewardEventStore();
  const dispatch = useDispatch();
  const experimentData = useLoginExperimentEventProps();
  const { pathname, query, push, asPath } = useRouter();
  const { mutate: updateSWRCache } = useSWRConfig();

  const setSelectedGoal = useUpdateSelectedGoal();

  const isContentPage = asPath?.startsWith('/content/');

  const { closeElement: closeLoginDrawer } = useToggleable(LOGIN_DRAWER);

  const { goalInfo: routerGoalInfo } = useGoalInfo(query?.goalUID);
  const isLoginTriggeredFromIframe = useIsLoginTriggeredFromIframe();

  const isRegistered = useLoginStore(getIsRegistered);
  const setIsLoggingIn = useLoginStore(getSetIsLoggingIn);

  const { courseUID, lessonUID } = query;
  const { youtubePlaylistData } = useYoutubeCourses(courseUID);

  return async (triggeredFromDetailsPage = false) => {
    const promises = [
      dispatch(fetchMyInfo(pathname)),
      dispatch(fetchPurchasedItems()),
      dispatch(fetchFollowedGoals())
    ];
    const result = await Promise.all(promises);

    const resp = result[0];
    const followedGoalsResp = result[2];
    const { selectedGoal, selectedGoalUid = null } = getSelectedGoalFromMeResp(
      resp,
      followedGoalsResp
    );
    if (resp?.rawResponse?.uid) {
      // Set user data in me SWR response if not already set
      const searchToken = resp.rawResponse.search_jwt_token;
      cookies.setCookie('searchToken', searchToken);
      const isLoginRoute = pathname === '/login';

      setUserDetailsBeforeEvent(resp.rawResponse, updateSWRCache);
      amplitude.setUserId(resp.rawResponse.uid);

      const validatedPresetRedirectURL =
        validatePresetRedirectURL(presetRedirectURL);
      if (isLoginRoute) {
        segment.track(LOGIN_SUCCESSFUL, {
          ...(experimentData || {}),
          goal_uid: selectedGoal?.uid,
          goal_name: selectedGoal?.name,
          is_new_user: !isRegistered,
          login_medium:
            loginType === constants.LOGIN_TYPE_EMAIL ? 'Email' : 'Mobile',
          redirect_to: validatedPresetRedirectURL || 'NA',
          proxy_id: `${country?.code}${mobileNumber}`,
          last_primary_source_section: segmentStorage.getItem('lpss') || 'NA',
          ...(selectedCity && { city: selectedCity })
        });
        if (!isRegistered) {
          segment.track(SIGNUP_SUCCESFUL, {
            goal_uid: selectedGoal?.uid,
            goal_name: selectedGoal?.name,
            is_new_user: !isRegistered,
            login_medium:
              loginType === constants.LOGIN_TYPE_EMAIL ? 'Email' : 'Mobile',
            redirect_to: validatedPresetRedirectURL || 'NA',
            proxy_id: `${country?.code}${mobileNumber}`
          });
        }

        sendIframeLoginSuccessMessage(query);
        sendPopupLoginSuccessMessage(query);

        if (validatedPresetRedirectURL)
          return push(window.location.origin + validatedPresetRedirectURL); // Keeping the redirect part of unacademy.com itself to avoid open-redirect vulnerability

        const redirectQuery = checkWhiteListed({ query });
        const staticPath = redirectQuery || '/';
        pushRoute(staticPath, staticPath);

        return;
      }

      closeLoginDrawer();
      setIsLoggingIn(false);

      if (followedGoalsResp.result) {
        checkIsFollowedGoal(
          followedGoalsResp.rawResponse,
          query.goalUID,
          dispatch
        );

        sendSegmentEventsPostLogin({
          meResp: resp,
          selectedGoalUid,
          selectedGoal,
          query,
          isContentPage,
          routerGoalInfo,
          triggeredFromDetailsPage,
          isLoginTriggeredFromIframe,
          isRegistered,
          loginType,
          country,
          mobileNumber,
          validatedPresetRedirectURL,
          lessonUID,
          youtubePlaylistData,
          experimentData,
          selectedCity
        });

        if (selectedGoalUid) {
          setSelectedGoal(selectedGoalUid);
          dispatch(fetchTopologyInfo(selectedGoalUid));
          const redirectionUrl = await getRedirectionURLAfterPostLogin({
            selectedGoalUid,
            selectedGoal,
            meRawResponse: resp.rawResponse,
            pathname
          });

          if (redirectionUrl && !presetRedirectURL)
            window.location.replace(redirectionUrl.as);
        }
      }

      if (validatePresetRedirectURL(presetRedirectURL))
        push(
          window.location.origin + validatePresetRedirectURL(presetRedirectURL)
        );
    }
    if (loginCallback) setTimeout(loginCallback, 500);
  };
};

export default usePostLoginActions;
